import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { notifyAdminSupport } from '../lib/notifyAdminSupport';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FeedbackForm({ isOpen, onClose }: FeedbackFormProps) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      showError(t('feedback.error.notLoggedIn'));
      return;
    }

    if (!title.trim() || !description.trim()) {
      showError(t('feedback.error.requiredFields'));
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: savedFeedback, error } = await supabase
        .from('feedback')
        .insert({
          user_id: user.id,
          title: title.trim(),
          feedback_text: description.trim(),
          status: 'pending'
        })
        .select('id, created_at')
        .single();

      if (error) {
        console.error('Error submitting feedback:', error);
        showError(t('feedback.error.submitFailed'));
        return;
      }

      try {
        await notifyAdminSupport({
          type: 'feedback',
          subject: title.trim(),
          message: description.trim(),
          from_email: user.email || 'N/A',
          from_name: user.email?.split('@')[0] || 'User',
          record_id: savedFeedback.id,
          created_at: savedFeedback.created_at,
        });
      } catch (notifyError) {
        console.error('Admin notification failed:', notifyError);
      }

      showSuccess(t('feedback.success.submitted'));
      handleClose();
    } catch (err) {
      console.error('Unexpected error submitting feedback:', err);
      showError(t('feedback.error.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      style={{
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 transform"
        style={{
          animation: 'scaleIn 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-orange-100">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">{t('feedback.title')}</h3>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="space-y-4">
            <div>
              <label htmlFor="feedback-title" className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedback.form.title')}
              </label>
              <input
                id="feedback-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                placeholder={t('feedback.form.titlePlaceholder')}
                disabled={isSubmitting}
                maxLength={200}
              />
            </div>

            <div>
              <label htmlFor="feedback-description" className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedback.form.description')}
              </label>
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors resize-none"
                placeholder={t('feedback.form.descriptionPlaceholder')}
                disabled={isSubmitting}
                maxLength={2000}
              />
              <p className="mt-1 text-xs text-gray-500">
                {description.length} / 2000 {t('feedback.form.characters')}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('buttons.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !title.trim() || !description.trim()}
              className="px-5 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('feedback.submitting') : t('buttons.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

